import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext';
import { projectService } from '../../services/projectService';
import protocolRegistrationService from '../../services/protocolRegistration.service';
import {
  getProjectIdentityFields,
  projectListPrimaryLabel,
  projectListSecondaryLabel,
} from '../../utils/projectIdentityFields';
import { fetchLatestClinicalIntakeData } from '../../services/clinicalIntake.service';

const RegistryDashboard = ({ projectId: projectIdProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProject, loadProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentProjectData, setCurrentProjectData] = useState(null);
  const [protocolRegistrationData, setProtocolRegistrationData] = useState(null);
  const [clinicalIntakeData, setClinicalIntakeData] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'detailed'

  // Get projectId from URL params, prop, or context
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('project');
  const projectId = projectIdFromUrl || projectIdProp || (currentProject?._id);

  // Fetch current project and protocol registration data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch current project if projectId is available
        if (projectId) {
          const projectResponse = await projectService.getProject(projectId);
          setCurrentProjectData(projectResponse.data);
          
          // Update context if needed
          if (!currentProject || currentProject._id !== projectId) {
            await loadProject(projectId);
          }

          // Fetch protocol registration data to get registry IDs
          try {
            const regData = await protocolRegistrationService.getByProject(projectId);
            setProtocolRegistrationData(regData);
          } catch (regErr) {
            // Protocol registration might not exist yet, that's okay
            console.log('No protocol registration data found:', regErr);
            setProtocolRegistrationData(null);
          }

          // Fetch clinical intake data to get study title
          try {
            const intakeData = await fetchLatestClinicalIntakeData(projectId);
            setClinicalIntakeData(intakeData);
          } catch (intakeErr) {
            console.log('No clinical intake data found:', intakeErr);
            setClinicalIntakeData(null);
          }
        } else if (currentProject) {
          setCurrentProjectData(currentProject);
          // Try to fetch protocol registration for current project
          try {
            const regData = await protocolRegistrationService.getByProject(currentProject._id);
            setProtocolRegistrationData(regData);
          } catch (regErr) {
            setProtocolRegistrationData(null);
          }

          // Try to fetch clinical intake for current project
          try {
            const intakeData = await fetchLatestClinicalIntakeData(currentProject._id);
            setClinicalIntakeData(intakeData);
          } catch (intakeErr) {
            setClinicalIntakeData(null);
          }
        } else {
          // No project selected
          setError('No study selected. Please select a study first.');
          setCurrentProjectData(null);
          setProtocolRegistrationData(null);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Failed to fetch project data');
        setCurrentProjectData(null);
        setProtocolRegistrationData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, currentProject, loadProject]);

  // Registry configuration matching ProtocolRegistration component
  const registryConfig = [
    {
      id: 'clinical-trials-gov', 
      label: 'clinical_trials.gov', 
      key: 'clinicalTrialsGov',
      // Treat unique_protocol_id as the registered identifier for CT.gov
      extractId: (regData) =>
        regData?.study_identification?.unique_protocol_id ||
        regData?.eu_registry?.nct_number || 
        regData?.study_identification?.nct_number || 
        'N/A'
    },
    {
      id: 'who', 
      label: 'WHO', 
      key: 'who',
      extractId: (regData) => regData?.who_registry?.primary_registry?.unique_id_number || 'N/A'
    },
    {
      id: 'eu', 
      label: 'European Union', 
      key: 'euCtis',
      extractId: (regData) => regData?.eu_registry?.protocol_code || 'N/A'
    },
    {
      id: 'germany', 
      label: 'Germany Trial Registry', 
      key: 'germany',
      extractId: (regData) => {
        // Check additional_registries for Germany
        const additionalRegistries = regData?.eu_registry?.additional_registries || [];
        const germanyReg = additionalRegistries.find(reg => 
          reg.registry_name && (
            reg.registry_name.toLowerCase().includes('germany') ||
            reg.registry_name.toLowerCase().includes('drks')
          )
        );
        return germanyReg?.registry_identifier || 'N/A';
      }
    },
    {
      id: 'india', 
      label: 'India', 
      key: 'india',
      extractId: (regData) => {
        // Check additional_registries for India
        const additionalRegistries = regData?.eu_registry?.additional_registries || [];
        const indiaReg = additionalRegistries.find(reg => 
          reg.registry_name && (
            reg.registry_name.toLowerCase().includes('india') ||
            reg.registry_name.toLowerCase().includes('ctri')
          )
        );
        return indiaReg?.registry_identifier || 'N/A';
      }
    },
    {
      id: 'japan', 
      label: 'Japan Trial Registry', 
      key: 'japan',
      extractId: (regData) => {
        // Check additional_registries for Japan
        const additionalRegistries = regData?.eu_registry?.additional_registries || [];
        const japanReg = additionalRegistries.find(reg => 
          reg.registry_name && (
            reg.registry_name.toLowerCase().includes('japan') ||
            reg.registry_name.toLowerCase().includes('pmda') ||
            reg.registry_name.toLowerCase().includes('jrct')
          )
        );
        return japanReg?.registry_identifier || 'N/A';
      }
    },
    {
      id: 'south-korea', 
      label: 'South Korea', 
      key: 'southKorea',
      extractId: (regData) => {
        // Check additional_registries for South Korea
        const additionalRegistries = regData?.eu_registry?.additional_registries || [];
        const koreaReg = additionalRegistries.find(reg => 
          reg.registry_name && (
            reg.registry_name.toLowerCase().includes('korea') ||
            reg.registry_name.toLowerCase().includes('cris')
          )
        );
        return koreaReg?.registry_identifier || 'N/A';
      }
    },
    {
      id: 'anz', 
      label: 'Australia & New Zealand', 
      key: 'anz',
      extractId: (regData) => {
        // Check additional_registries for ANZ
        const additionalRegistries = regData?.eu_registry?.additional_registries || [];
        const anzReg = additionalRegistries.find(reg => 
          reg.registry_name && (
            reg.registry_name.toLowerCase().includes('australia') ||
            reg.registry_name.toLowerCase().includes('new zealand') ||
            reg.registry_name.toLowerCase().includes('anzctr')
          )
        );
        return anzReg?.registry_identifier || 'N/A';
      }
    },
  ];

  // Heuristic: check if user has started filling data for a given registry
  const hasProgressForRegistry = (registryKey, regData) => {
    if (!regData) return false;

    switch (registryKey) {
      case 'clinicalTrialsGov':
        // Any core CT.gov fields or generated XML means "in progress"
        return Boolean(
          regData.study_identification?.unique_protocol_id ||
          regData.study_identification?.brief_title ||
          regData.study_status?.overall_recruitment_status ||
          regData.clinicaltrials_xml
        );

      case 'who':
        // WHO primary registry basics or recruitment/status fields
        return Boolean(
          regData.who_registry?.primary_registry?.name ||
          regData.who_registry?.primary_registry?.unique_id_number ||
          regData.who_registry?.registration_date ||
          regData.who_registry?.recruitment_status
        );

      case 'euCtis':
        // EU CTIS core identifiers or key trial metadata
        return Boolean(
          regData.eu_registry?.full_title_english ||
          regData.eu_registry?.public_title_english ||
          regData.eu_registry?.protocol_code ||
          regData.eu_registry?.trial_phase ||
          (Array.isArray(regData.eu_registry?.products) && regData.eu_registry.products.length > 0)
        );

      // Country‑specific additional registries – if any matching entry exists, treat as "in progress"
      case 'germany':
      case 'india':
      case 'japan':
      case 'southKorea':
      case 'anz': {
        const additionalRegistries = regData.eu_registry?.additional_registries || [];
        return additionalRegistries.length > 0;
      }

      default:
        return false;
    }
  };

  // Extract registry status from protocol registration data - dynamically based on registryConfig
  const getRegistryStatusFromProtocolRegistration = (regData) => {
    const registries = {};
    let completedCount = 0;

    registryConfig.forEach((registry) => {
      const extractedId = registry.extractId(regData);
      const hasRealData = extractedId !== 'N/A' && extractedId.trim() !== '';
      const hasProgress = hasProgressForRegistry(registry.key, regData);

      if (hasRealData) {
        // Registered: we have a real registry identifier
        completedCount++;
        registries[registry.key] = {
          id: extractedId,
          status: 'Registered',
          statusType: 'success',
          label: registry.label,
        };
      } else if (hasProgress) {
        // In Progress: user has started filling fields / generating XML, but no final ID yet
        registries[registry.key] = {
          id: 'N/A',
          status: 'In Progress',
          statusType: 'warning',
          label: registry.label,
        };
      } else {
        // Not Started: no meaningful data yet
        registries[registry.key] = {
          id: 'N/A',
          status: 'Not Registered',
          statusType: 'default',
          label: registry.label,
        };
      }
    });

    const totalRegistries = registryConfig.length;
    const percentage = (completedCount / totalRegistries) * 100;

    return {
      ...registries,
      globalSyncStatus: {
        percentage: Math.round(percentage),
        status:
          percentage === 100
            ? 'Complete'
            : completedCount > 0
            ? 'In Progress'
            : 'Not Started',
        statusType:
          percentage === 100
            ? 'success'
            : completedCount > 0
            ? 'warning'
            : 'default',
      },
    };
  };

  // Get study title from clinical intake data, fallback to project name
  const getStudyTitle = () => {
    if (clinicalIntakeData?.study_identification?.preferred_short_title) {
      return clinicalIntakeData.study_identification.preferred_short_title;
    }
    if (clinicalIntakeData?.study_identification?.protocol_number) {
      return `Protocol ${clinicalIntakeData.study_identification.protocol_number}`;
    }
    return getProjectIdentityFields(currentProjectData).trialAcronym || 'Untitled Trial';
  };

  // Get active registries (those that should be shown in the table)
  const activeRegistries = registryConfig.filter(reg => true);

  // Transform current project to study registry data format
  const studyRegistryData = currentProjectData ? (() => {
    const registryStatus = getRegistryStatusFromProtocolRegistration(protocolRegistrationData);
    const identity = getProjectIdentityFields(currentProjectData);
    return [{
      sponsorProtocolIdentifier: identity.sponsorProtocolIdentifier || currentProjectData._id,
      trialAcronym: identity.trialAcronym || getStudyTitle(),
      sponsorName: identity.sponsorName,
      studyTitle: getStudyTitle(),
      projectId: currentProjectData._id,
      registries: registryStatus,
      globalSyncStatus: registryStatus.globalSyncStatus,
    }];
  })() : [];

  // Derive real stats for detailed view from current registry status
  const deriveStats = () => {
    if (!studyRegistryData.length) {
      return {
        totalRegistrations: 0,
        pendingSubmissions: 0,
        completed: 0,
        complianceRate: 0,
      };
    }
    const study = studyRegistryData[0];
    const regStatuses = study.registries || {};
    const registryKeys = activeRegistries.map(r => r.key);

    let completedCount = 0;
    registryKeys.forEach(key => {
      if (regStatuses[key]?.statusType === 'success') {
        completedCount += 1;
      }
    });

    const total = registryKeys.length || 1;
    const pending = total - completedCount;

    return {
      totalRegistrations: completedCount,
      pendingSubmissions: pending,
      completed: completedCount,
      complianceRate: Math.round((completedCount / total) * 100),
    };
  };

  const stats = deriveStats();

  // Handle study click - switch to detailed view
  const handleStudyClick = (studyProjectId) => {
    setViewMode('detailed');
  };

  // Handle back to table view
  const handleBackToTable = () => {
    setViewMode('table');
  };


  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case 'success':
        return '✅';
      case 'warning':
        return '🟡';
      case 'error':
        return '❌';
      default:
        return '⚪';
    }
  };

  const getStatusColor = (statusType) => {
    switch (statusType) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default'; // Material-UI supports 'default' for Chip
    }
  };

  // Build real-time snapshot activity and per-registry summary from current status
  const currentRegistryStatuses = studyRegistryData.length ? studyRegistryData[0].registries || {} : {};

  const recentActivity = activeRegistries.map((registry, index) => {
    const statusObj = currentRegistryStatuses[registry.key] || {
      status: 'Not Registered',
      statusType: 'default',
    };
    return {
      id: index + 1,
      action: `${registry.label} status: ${statusObj.status}`,
      date: new Date().toLocaleDateString(),
      status: statusObj.statusType === 'success' ? 'completed' : 'pending',
    };
  });

  const registrationsByRegistry = activeRegistries.map((registry) => {
    const statusObj = currentRegistryStatuses[registry.key] || {
      status: 'Not Registered',
      statusType: 'default',
    };
    const hasRegistration = statusObj.statusType === 'success';
    return {
      name: registry.label,
      count: hasRegistration ? 1 : 0,
      status: hasRegistration ? 'completed' : 'pending',
    };
  });

  const StatCard = ({ title, value, icon: Icon, color = 'primary' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" color={color}>
              {value}
            </Typography>
          </Box>
          <Icon 
            sx={{ 
              fontSize: 40, 
              color: (theme) => theme.palette[color]?.main || theme.palette.primary.main, 
              opacity: 0.7 
            }} 
          />
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Render table view
  const renderTableView = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Protocol Registry Management Dashboard
        {currentProjectData && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Current Study: {getStudyTitle()}
          </Typography>
        )}
      </Typography>

      {/* Study Registry Status Table */}
      <Paper sx={{ p: 3, mb: 3, overflowX: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Study Registry Status
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 1000 }} aria-label="study registry status table" size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Trial Acronym & Sponsor</strong></TableCell>
                {activeRegistries.map((registry) => (
                  <TableCell key={registry.id}><strong>{registry.label}</strong></TableCell>
                ))}
                <TableCell><strong>Global Sync Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studyRegistryData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeRegistries.length + 2} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No studies found. Please create a study first.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                studyRegistryData.map((study, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body1"
                      onClick={() => handleStudyClick(study.projectId)}
                      sx={{
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: 'primary.dark',
                        },
                        fontWeight: 'bold',
                        display: 'block',
                        maxWidth: '200px',
                      }}
                    >
                      <strong>{study.trialAcronym || study.studyTitle}</strong>
                      <br />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: '200px',
                        }}
                        title={study.sponsorName || study.sponsorProtocolIdentifier}
                      >
                        {study.sponsorName || study.sponsorProtocolIdentifier || '—'}
                      </Typography>
                    </Link>
                  </TableCell>
                  {activeRegistries.map((registry) => {
                    const registryData = study.registries[registry.key] || { 
                      id: 'N/A', 
                      status: 'Not Registered', 
                      statusType: 'default' 
                    };
                    return (
                      <TableCell key={registry.id}>
                        <Box>
                          <Typography variant="body2" component="span">
                            {getStatusIcon(registryData.statusType)}{' '}
                            {registryData.id}
                          </Typography>
                          <br />
                          <Chip
                            label={registryData.status}
                            color={getStatusColor(registryData.statusType)}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" component="span" fontWeight="bold">
                        {study.globalSyncStatus.percentage}% {study.globalSyncStatus.status}
                      </Typography>
                      <br />
                      <LinearProgress
                        variant="determinate"
                        value={study.globalSyncStatus.percentage}
                        color={getStatusColor(study.globalSyncStatus.statusType) === 'default' ? 'primary' : getStatusColor(study.globalSyncStatus.statusType)}
                        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // Render detailed dashboard view
  const renderDetailedView = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {projectListPrimaryLabel(currentProjectData)}
        </Typography>
        <Link
          component="button"
          onClick={handleBackToTable}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          ← Back to Table
        </Link>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Registrations"
            value={stats.totalRegistrations}
            icon={AssignmentIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Submissions"
            value={stats.pendingSubmissions}
            icon={PendingIcon}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed"
            value={stats.completed}
            icon={CheckCircleIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Compliance Rate
                  </Typography>
                  <Typography variant="h4" component="div" color="info.main">
                    {stats.complianceRate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={stats.complianceRate}
                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    color="info"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Recent Activity</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              {recentActivity.map((activity) => (
                <Box
                  key={activity.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box>
                    <Typography variant="body1">{activity.action}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activity.date}
                    </Typography>
                  </Box>
                  <Chip
                    label={activity.status}
                    color={activity.status === 'completed' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Registrations by Registry */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Registrations by Registry
            </Typography>
            <Box sx={{ mt: 2 }}>
              {registrationsByRegistry.map((registry, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body1">{registry.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {registry.count} registration
                    </Typography>
                    <Chip
                      label={registry.status}
                      color={registry.status === 'completed' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // Main render - conditionally show table or detailed view
  return (
    <Box sx={{ p: 2 }}>
      {viewMode === 'table' ? renderTableView() : renderDetailedView()}
    </Box>
  );
};

export default RegistryDashboard;

